import { useEffect, useRef, useState } from 'react'
import { searchProducts } from './pesticides'
import { PickerInput, type PickerOption } from './picker-input'
import type { PesticideProduct } from './types'

/** What picking a registered product records on the entry. Typing a name instead leaves these empty. */
export type PickedProduct = { productId: string; pcpNumber: string; activeIngredients: string }

const titleCase = (s: string) => s.toLowerCase().replace(/(^|[\s(/-])([a-z])/g, (_, a: string, b: string) => a + b.toUpperCase())
export const tidy = (s: string) => titleCase(s.replace(/\s+/g, ' ').trim())
export const ingredients = (p: PesticideProduct) =>
  (p.activeIngredientsRawEn ?? '')
    .split(';')
    .map((s) => tidy(s))
    .filter(Boolean)
    .join(', ')

/** A registered product as a picker option: PCP first, since the end of a narrow line is cut off. */
export const registryOption = (p: PesticideProduct): PickerOption => ({
  id: p.id,
  label: tidy(p.productNameEn),
  detail: [`PCP ${p.registrationNumber}`, ingredients(p), p.registrantName && tidy(p.registrantName)].filter(Boolean).join(' · '),
})

/** What a registry result records on an entry when picked. */
export const pickedFrom = (p: PesticideProduct): { productName: string } & PickedProduct => ({
  productName: tidy(p.productNameEn),
  productId: p.id,
  pcpNumber: p.registrationNumber,
  activeIngredients: ingredients(p),
})

/**
 * Searches Health Canada's registry (current blueberry products first, or `all`) shortly after the
 * name stops changing, while `active`. Answers that arrive after a newer request are ignored.
 */
export function useRegistrySearch(name: string, { all, active }: { all: boolean; active: boolean }) {
  const [results, setResults] = useState<PesticideProduct[]>([])
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const request = useRef(0)

  useEffect(() => {
    if (!active) return
    const id = ++request.current
    if (name.trim().length < 2) {
      setResults([])
      setLoading(false)
      return
    }
    setLoading(true)
    const timer = setTimeout(() => {
      searchProducts(name, { allProducts: all }).then(
        (found) => {
          if (id !== request.current) return
          setResults(found)
          setFailed(false)
          setLoading(false)
        },
        () => {
          if (id !== request.current) return
          setFailed(true)
          setLoading(false)
        },
      )
    }, 250)
    return () => clearTimeout(timer)
  }, [name, all, active])

  return { results, loading, failed, options: results.map(registryOption) }
}

/**
 * The product name box for a spray: type to search Health Canada's registry (current blueberry
 * products first), tap a result to link the entry to that registration (id and PCP number), or
 * just keep typing for a product that isn't listed. Editing the text after picking unlinks it.
 * Wrap it in a Catalyst `Field` with a `Label`.
 */
export function ProductPicker({
  name,
  picked,
  onName,
  onPick,
  ...inputProps
}: {
  name: string
  /** The registered product this entry is linked to, if any. */
  picked: Partial<PickedProduct> | null
  onName: (name: string) => void
  onPick: (product: { productName: string } & PickedProduct) => void
} & Pick<React.ComponentPropsWithoutRef<'input'>, 'placeholder' | 'required' | 'autoFocus'> & { invalid?: boolean }) {
  const [all, setAll] = useState(false)
  // Search once the box is used (focused or typed in), not for every name an entry opens with.
  const [used, setUsed] = useState(false)
  const linked = !!picked?.productId
  const { results, loading, failed, options } = useRegistrySearch(name, { all, active: used && !linked })

  return (
    <PickerInput
      {...inputProps}
      value={name}
      onFocus={() => setUsed(true)}
      onChange={(text) => {
        setUsed(true)
        onName(text)
      }}
      onPick={(option) => onPick(pickedFrom(results.find((r) => r.id === option.id)!))}
      groups={[{ options }]}
      linked={
        linked
          ? ['Registered product', picked?.pcpNumber && `PCP ${picked.pcpNumber}`, picked?.activeIngredients].filter(Boolean).join(' · ')
          : null
      }
      minChars={2}
      busy={loading}
      listLabel="Matching products"
      emptyText={failed ? 'Couldn’t search the registry. You can still type the name.' : 'No match. Keep typing the name, or search all products.'}
      footerAction={{
        label: all ? 'Only current blueberry products' : 'Search all products',
        onClick: () => {
          setUsed(true)
          setAll((a) => !a)
        },
      }}
    />
  )
}
